
import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { Alert, Box, Checkbox, FormControlLabel, Link, Snackbar, Stack, Typography } from "@mui/material";
import { InvokeAPI } from "../../apis/API";
import { getUserMgmtBase } from "../../config/apiBase";
import AsyncButton from "../common/AsyncButton";
import { useAsyncAction } from "../common/useAsyncAction";


const Signup = ({ referralCode = "0" }) => {
  const [signupData, setSignupData] = useState({
    emailid: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const { run, loading: submitting } = useAsyncAction();


  const handleSignupChange = (e) => {
    e.preventDefault();
    const { name, value } = e.target;
    setSignupData({ ...signupData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setSignupData({ ...signupData, [name]: checked });
  }

  const [showPassword, setShowPassword] = useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  const { login } = useAuth();
  const navigate = useNavigate();

  const [state, setState] = useState({ open: false, message: "" })

  const handleClose = () => {
    setState({ ...state, open: false });
  };

  const validateForm = () => {
    const newErrors = {};
    const emailid = (signupData.emailid || "").trim();
    const password = signupData.password || "";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailid) {
      newErrors.emailid = "Email is required";
    } else if (!emailRegex.test(emailid)) {
      newErrors.emailid = "Please enter a valid email address";
    }

    if (!password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSignup = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const emailid = (signupData.emailid || "").trim();
    const password = signupData.password || "";

    await run(async () => {
      const uinfo = await InvokeAPI(`${getUserMgmtBase()}/v1/user/create`,
        { "EmailId": emailid, "Password": password, "ReferredCode": referralCode },
        'POST',
        null,
        false)
      if (uinfo.alreadyExists) {
        setState({ open: true, message: "Email already exists" });
      } else {
        const resp = await login(emailid, password)
        if (resp === 0) {
          navigate("/app/profile");
        }
      }
    });
  };

  /*
        <p></p>
      <FormControlLabel
        control={
          <Checkbox checked={signupData.parent} onChange={handleCheckboxChange} name="parent" />
        }
        label="Parent"
      />

      <FormControlLabel
        control={
          <Checkbox checked={signupData.teacher} onChange={handleCheckboxChange} name="teacher" />
        }
        label="Teacher"
      />
  */

  return (
    <form>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={state.open}
        onClose={handleClose}
        key={state.message}
        TransitionProps={{ timeout: 300 }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          top: { xs: 72, sm: 80 },
        }}
      >
        <Alert 
          variant="filled" 
          severity="warning"
          sx={{
            animation: 'slideUp 0.3s ease-out',
            '@keyframes slideUp': {
              from: {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {state.message}
        </Alert>
      </Snackbar>
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center"
        sx={{
          mb: 3,
          animation: 'fadeIn 0.5s ease-in',
          '@keyframes fadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
          Let's Get Started!
        </Typography>
      </Box>
      <TextField
        label="Email"
        variant="outlined"
        fullWidth
        name="emailid"
        value={signupData.emailid}
        onChange={handleSignupChange}
        required
        autoComplete="email"
        error={!!errors.emailid}
        helperText={errors.emailid}
        disabled={submitting}
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
        }}
      />
      <TextField
        id="outlined-adornment-password"
        type={showPassword ? 'text' : 'password'}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? 'hide the password' : 'display the password'
                  }
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                  disabled={submitting}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
        label="Password"
        name="password"
        value={signupData.password}
        onChange={handleSignupChange}
        required
        fullWidth
        error={!!errors.password}
        helperText={errors.password}
        disabled={submitting}
        sx={{ 
          mb: 2,
          '& .MuiOutlinedInput-root': {
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-1px)',
            },
          },
        }}
      />
      <Box display="flex" justifyContent="center" alignItems="center">
        <Stack spacing={2} sx={{ width: '100%' }}>
          <Typography sx={{ fontSize: "0.75rem", textAlign: 'center' }}>
            By clicking signup button you agree to our <Link sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }} href="/tnc" target="_blank" rel="noopener noreferrer">Terms of Use</Link> and you are 18 or above years of age.
          </Typography>
          <AsyncButton 
            type="submit" 
            variant="contained" 
            color="primary" 
            onClick={handleSignup}
            loading={submitting}
            fullWidth
            sx={{
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)',
              },
            }}
          >
            Signup
          </AsyncButton>
        </Stack>
      </Box>
    </form>
  );
};

export default Signup;
